import { useState } from 'react'
import { BellIcon, ChevronRightIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { AccommodationDetail } from '@/api/accommodation/types'
import { trackLocationWatchmanOpened } from '@/lib/track'
import { LOCATION_WATCHMAN_TITLE, LocationWatchmanDialog } from './LocationWatchmanDialog'

const EMPTY_STATE_SUBTEXT = 'Zrovna není volno? Nechte nám e-mail a ozveme se, jakmile se termín uvolní.'

interface LocationWatchmanSheetProps {
  accommodation: AccommodationDetail
  /**
   * `panel` renders a standalone card sitting next to the price guarantee — the primary entry point.
   * `emptyState` renders a centered CTA with stronger copy, used when no dates are available at all.
   */
  variant?: 'default' | 'panel' | 'emptyState'
}

export function LocationWatchmanSheet({ accommodation, variant = 'default' }: LocationWatchmanSheetProps) {
  const [open, setOpen] = useState(false)

  const openSheet = () => {
    trackLocationWatchmanOpened(accommodation, variant)
    setOpen(true)
  }

  const panelTrigger = (
    <button
      type="button"
      onClick={openSheet}
      className="flex w-full items-center gap-2.5 rounded-xl border border-green-200 bg-white px-3 py-2.5 text-left transition-colors hover:bg-green-100/40"
    >
      <span className="relative shrink-0">
        <BellIcon className="size-5 text-green-600" />
        <span className="bg-red absolute -top-0.5 -right-0.5 size-2 rounded-full ring-2 ring-white" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-green-700">Nenalezli jste vhodný termín?</div>
        <div className="truncate text-xs text-slate-500">Dáme vám vědět, až se termín uvolní.</div>
      </div>
      <ChevronRightIcon className="size-4 shrink-0 text-green-700" />
    </button>
  )

  const emptyStateTrigger = (
    <div className="flex flex-col items-center gap-3 text-center">
      <p className="max-w-xs text-sm text-slate-600">{EMPTY_STATE_SUBTEXT}</p>
      <Button type="button" variant="red" onClick={openSheet}>
        <BellIcon className="size-4" />
        {LOCATION_WATCHMAN_TITLE}
      </Button>
    </div>
  )

  const defaultTrigger = (
    <Button type="button" variant="subtle" size="sm" className="w-full" onClick={openSheet}>
      <BellIcon className="size-4" />
      {LOCATION_WATCHMAN_TITLE}
    </Button>
  )

  const triggerButton =
    variant === 'panel' ? panelTrigger : variant === 'emptyState' ? emptyStateTrigger : defaultTrigger

  return (
    <>
      {triggerButton}
      <LocationWatchmanDialog
        slug={accommodation.slug}
        unitId={accommodation.unit.id}
        source={variant}
        open={open}
        onOpenChange={setOpen}
      />
    </>
  )
}
